import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function LiveMonitoringPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [industryDropdownOpen, setIndustryDropdownOpen] = useState(false);

  const industries = [
    {
      id: 'construction',
      name: 'Construction Sites',
      icon: 'ri-building-2-line',
      image: 'https://public.readdy.ai/ai/img_res/f73db3722c7f8218e15060c96710f6f0.jpg',
      description: 'Protect valuable equipment, materials, and prevent unauthorized access to active construction zones.',
      challenges: ['Equipment theft & vandalism', 'After-hours intrusions', 'Safety compliance monitoring', 'Material theft prevention'],
      solutions: ['24/7 live monitoring with instant alerts', 'Virtual guard tours every 30 minutes', 'Audio talk-down capabilities', 'Time-lapse documentation for project tracking']
    },
    {
      id: 'dealerships',
      name: 'Car Dealerships',
      icon: 'ri-car-line',
      image: 'https://readdy.ai/api/search-image?query=luxury%20car%20dealership%20lot%20at%20night%20with%20rows%20of%20vehicles%20under%20bright%20security%20lighting%20professional%20automotive%20showroom%20exterior%20modern%20architecture%20with%20glass%20windows%20and%20premium%20vehicles%20on%20display&width=800&height=600&seq=dealer1&orientation=landscape',
      description: 'Safeguard your inventory of high-value vehicles from theft, vandalism, and unauthorized test drives.',
      challenges: ['Vehicle theft & break-ins', 'Catalytic converter theft', 'Vandalism & graffiti', 'Unauthorized lot access'],
      solutions: ['License plate recognition integration', 'Perimeter breach detection', 'Real-time video verification', 'Instant police dispatch coordination']
    },
    {
      id: 'trucking',
      name: 'Trucking Yards',
      icon: 'ri-truck-line',
      image: 'https://readdy.ai/api/search-image?query=large%20trucking%20yard%20at%20night%20with%20semi%20trucks%20and%20trailers%20parked%20in%20rows%20industrial%20logistics%20facility%20with%20security%20fencing%20and%20bright%20overhead%20lighting%20professional%20transportation%20depot&width=800&height=600&seq=truck1&orientation=landscape',
      description: 'Monitor fleet vehicles, cargo, and facility access points around the clock.',
      challenges: ['Cargo theft & tampering', 'Fuel theft', 'Unauthorized vehicle movement', 'Driver safety concerns'],
      solutions: ['Gate access monitoring & control', 'Cargo area surveillance', 'Driver check-in verification', 'Incident documentation & reporting']
    },
    {
      id: 'condos',
      name: 'Condominiums',
      icon: 'ri-community-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20luxury%20condominium%20building%20exterior%20at%20twilight%20with%20elegant%20entrance%20lobby%20visible%20through%20glass%20doors%20upscale%20residential%20tower%20with%20landscaped%20grounds%20and%20secure%20parking%20entrance&width=800&height=600&seq=condo1&orientation=landscape',
      description: 'Enhance resident safety with professional monitoring of common areas, parking, and entry points.',
      challenges: ['Package theft', 'Unauthorized building access', 'Parking lot incidents', 'Common area misuse'],
      solutions: ['Lobby & entrance monitoring', 'Parking garage surveillance', 'Visitor verification system', 'Emergency response coordination']
    },
    {
      id: 'cannabis',
      name: 'Cannabis Facilities',
      icon: 'ri-plant-line',
      image: 'https://readdy.ai/api/search-image?query=modern%20secure%20cannabis%20cultivation%20facility%20exterior%20at%20night%20with%20high%20security%20fencing%20and%20controlled%20access%20gates%20industrial%20agricultural%20building%20with%20professional%20lighting%20and%20surveillance%20equipment%20visible&width=800&height=600&seq=cannabis1&orientation=landscape',
      description: 'Meet strict regulatory compliance requirements with comprehensive video surveillance and monitoring.',
      challenges: ['Regulatory compliance (90-day retention)', 'Product diversion prevention', 'Restricted area access control', 'Seed-to-sale tracking support'],
      solutions: ['Compliance-ready recording systems', 'Multi-zone access monitoring', 'Detailed audit trail documentation', 'Integration with POS systems']
    },
    {
      id: 'industrial',
      name: 'High-Value Industrial Sites',
      icon: 'ri-building-4-line',
      image: 'https://readdy.ai/api/search-image?query=large%20industrial%20manufacturing%20facility%20at%20night%20with%20security%20perimeter%20fencing%20and%20bright%20flood%20lights%20modern%20factory%20complex%20with%20loading%20docks%20and%20equipment%20storage%20areas%20professional%20commercial%20property&width=800&height=600&seq=industrial1&orientation=landscape',
      description: 'Protect critical infrastructure, expensive machinery, and sensitive operations from threats.',
      challenges: ['Equipment & copper theft', 'Industrial espionage', 'Safety incident monitoring', 'Perimeter security'],
      solutions: ['Thermal imaging integration', 'Multi-site centralized monitoring', 'Emergency response protocols', 'Detailed incident reporting']
    }
  ];

  const monitoringFeatures = [
    {
      icon: 'ri-eye-line',
      title: 'Live Video Monitoring',
      description: 'Trained security professionals watch your cameras in real-time, ready to respond to any suspicious activity instantly.'
    },
    {
      icon: 'ri-volume-up-line',
      title: 'Audio Talk-Down',
      description: 'Our operators can issue verbal warnings through on-site speakers to deter intruders before they cause damage.'
    },
    {
      icon: 'ri-alarm-warning-line',
      title: 'Instant Alert Response',
      description: 'When motion is detected, our team verifies the threat and contacts authorities within seconds if needed.'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Detailed Reporting',
      description: 'Receive comprehensive incident reports with video clips, timestamps, and actions taken for your records.'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobile Access',
      description: 'View live feeds and recorded footage from anywhere using our secure mobile app or web portal.'
    },
    {
      icon: 'ri-cloud-line',
      title: 'Cloud Storage',
      description: 'All footage is securely stored in the cloud with customizable retention periods to meet your compliance needs.'
    }
  ];

  const faqs = [
    {
      question: 'How does live video monitoring differ from traditional security cameras?',
      answer: 'Traditional cameras simply record footage for later review. Live video monitoring means trained security professionals are actively watching your cameras 24/7, ready to respond immediately to any suspicious activity. They can issue audio warnings, contact authorities, and document incidents in real-time.'
    },
    {
      question: 'What happens when suspicious activity is detected?',
      answer: 'When our monitoring team detects suspicious activity, they immediately assess the situation using multiple camera angles. If a threat is confirmed, they can issue audio warnings through on-site speakers, contact you or your designated contacts, and dispatch local authorities—all within seconds of detection.'
    },
    {
      question: 'Can live monitoring help with regulatory compliance?',
      answer: 'Absolutely. Our systems are designed to meet strict regulatory requirements, including those for cannabis facilities that require 90-day video retention. We provide detailed audit trails, incident documentation, and can integrate with your existing compliance systems.'
    },
    {
      question: 'What type of cameras do you recommend for live monitoring?',
      answer: 'We recommend high-resolution IP cameras with night vision, wide dynamic range, and analytics capabilities. The specific cameras depend on your site\'s unique needs—we\'ll conduct a thorough assessment and design a custom solution that provides optimal coverage.'
    },
    {
      question: 'Is live monitoring more cost-effective than on-site security guards?',
      answer: 'In most cases, yes. Live video monitoring typically costs 50-70% less than traditional security guards while providing superior coverage. One monitoring operator can watch multiple cameras across your entire property simultaneously, something impossible for a single guard.'
    }
  ];

  const scrollToContact = () => {
    const el = document.getElementById('consultation-form');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = '/#contact';
    }
  };

  const industryOptions = [
    'Construction Sites',
    'Car Dealerships',
    'Trucking Yards',
    'Condominiums',
    'Cannabis Facilities',
    'Industrial Sites',
    'Retail / Commercial',
    'Other'
  ];

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    formData.set('industry', selectedIndustry);

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      setFormStatus('error');
      return;
    }

    try {
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        urlEncoded.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d6b1r9dt1k61917cn5ng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setSelectedIndustry('');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  const monitoringStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://alliancesecuritysystems.com/' },
        { '@type': 'ListItem', position: 2, name: 'Live Monitoring', item: 'https://alliancesecuritysystems.com/live-monitoring' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://alliancesecuritysystems.com/live-monitoring#service',
      name: '24/7 Live Video Monitoring',
      description: 'Professional live video monitoring for construction sites, car dealerships, trucking yards, condominiums, cannabis facilities, and industrial properties. Real-time surveillance with trained security operators, audio talk-down, and instant police dispatch.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Alliance Security Systems',
        telephone: '+1-888-458-9181',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2355 Derry Road East, Unit 1',
          addressLocality: 'Mississauga',
          addressRegion: 'ON',
          postalCode: 'L5S 1V6',
          addressCountry: 'CA',
        },
      },
      areaServed: { '@type': 'AdministrativeArea', name: 'Greater Toronto Area' },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Live Monitoring Services',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Construction Site Monitoring', description: '24/7 live surveillance for construction sites with virtual guard tours, audio talk-down, and equipment theft prevention.' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Car Dealership Monitoring', description: 'Real-time video monitoring for dealership lots with license plate recognition and perimeter breach detection.' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Trucking Yard Monitoring', description: 'Gate access monitoring, cargo surveillance, and driver check-in verification for trucking and logistics facilities.' },
          },
          {
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: 'Cannabis Facility Monitoring', description: 'Compliance-ready 90-day video retention, multi-zone access monitoring, and detailed audit trail documentation.' },
          },
        ],
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '500',
        bestRating: '5',
      },
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Live Video Monitoring | 24/7 Surveillance for High-Risk Industries | Alliance Security Systems"
        description="Professional live video monitoring for construction sites, dealerships, trucking yards, condos, cannabis facilities & industrial properties. Real-time surveillance with trained security professionals. Audio talk-down, instant alerts & police dispatch. Get your free site assessment."
        keywords="live video monitoring, 24/7 surveillance, construction site security, dealership security, trucking yard monitoring, cannabis facility security, industrial security, remote monitoring"
        canonical="/live-monitoring"
        structuredData={monitoringStructuredData}
        faqItems={faqs}
      />

      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex flex-col leading-none">
              <span className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
              <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Home
            </Link>
            <Link to="/alarm-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Alarm Systems
            </Link>
            <Link to="/camera-packages" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Cameras
            </Link>
            <Link to="/clients" className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer">
              Clients
            </Link>
            <a 
              href="tel:18884589181"
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              1-888-458-9181
            </a>
            <button 
              onClick={scrollToContact}
              className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
            >
              Get Free Quote
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
            <Link to="/" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Home</Link>
            <Link to="/alarm-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Alarm Systems</Link>
            <Link to="/camera-packages" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Cameras</Link>
            <Link to="/access-control" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Access Control</Link>
            <Link to="/clients" className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50">Clients</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"><i className="ri-phone-line"></i>1-888-458-9181</a>
            <button onClick={scrollToContact} className="w-full bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2">Get Free Quote</button>
          </div>
        )}
      </nav>

      <main>
        {/* Hero Section */}
        <section className="relative py-16 sm:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://readdy.ai/api/search-image?query=professional%20security%20monitoring%20center%20with%20multiple%20large%20screens%20displaying%20surveillance%20camera%20feeds%20dark%20room%20with%20blue%20ambient%20lighting%20operators%20watching%20monitors%20high%20tech%20command%20center%20atmosphere&width=1920&height=1080&seq=livehero1&orientation=landscape" 
              alt="Live Monitoring Center"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-[#0A1628]/60"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
            <nav className="flex items-center gap-2 text-xs sm:text-sm text-white/60 mb-8" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-[#D4A853] cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#D4A853]">Live Video Monitoring</span>
            </nav>

            <div className="mb-8">
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap border border-white/20"
              >
                <i className="ri-arrow-left-line text-lg"></i>
                Back to Home
              </Link>
            </div>

            <div className="max-w-3xl">
              <span className="inline-block bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold mb-6 whitespace-nowrap">
                24/7 PROFESSIONAL MONITORING
              </span>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
                Live Video Monitoring for High-Risk Industries
              </h1>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Protect your construction sites, dealerships, trucking yards, condos, cannabis facilities, and industrial properties with real-time surveillance by trained security professionals.
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <button 
                  onClick={scrollToContact}
                  className="bg-[#D4A853] text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
                >
                  Get Free Site Assessment
                  <span className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-lg"></i>
                  </span>
                </button>
                <div className="flex items-center gap-3 text-white/80">
                  <a 
                    href="tel:18884589181"
                    className="flex items-center gap-3 hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-phone-line text-xl text-[#D4A853]"></i>
                    <span className="text-lg font-semibold">1-888-458-9181</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#0A1628] py-8" aria-label="Statistics">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                { stat: '500+', label: 'Sites Monitored' },
                { stat: '< 30 sec', label: 'Average Response Time' },
                { stat: '24/7/365', label: 'Live Monitoring' },
                { stat: '70%', label: 'Cost Savings vs Guards' }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl font-bold text-[#D4A853] mb-1">{item.stat}</p>
                  <p className="text-sm text-white/70">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="py-24 bg-white" aria-labelledby="industries-heading">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                INDUSTRIES WE SERVE
              </span>
              <h2 id="industries-heading" className="text-5xl font-bold text-[#0A1628] mb-4">
                Specialized Security for Your Industry
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                We understand the unique security challenges facing high-value and high-risk industries. Our tailored monitoring solutions address your specific needs.
              </p>
            </div>

            <div className="space-y-16">
              {industries.map((industry, index) => (
                <div 
                  key={industry.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="relative w-full h-[250px] sm:h-[400px] rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                      <img 
                        src={industry.image}
                        alt={industry.name}
                        className="w-full h-full object-cover object-center"
                      />
                      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-[#D4A853] text-white px-4 sm:px-6 py-2 sm:py-2 rounded-full text-xs sm:text-sm font-semibold flex items-center gap-2 whitespace-nowrap">
                        <i className={`${industry.icon} text-base sm:text-lg`}></i>
                        {industry.name}
                      </div>
                    </div>
                  </div>

                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <h3 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-4 sm:mb-6">{industry.name}</h3>
                    <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 leading-relaxed">{industry.description}</p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-6 mb-8 sm:mb-8">
                      <div className="bg-red-50 rounded-xl sm:rounded-2xl p-6 sm:p-8">
                        <h4 className="text-sm sm:text-base font-bold text-red-800 mb-4 sm:mb-4 flex items-center gap-2">
                          <i className="ri-error-warning-line text-lg sm:text-xl"></i>
                          Common Challenges
                        </h4>
                        <ul className="space-y-2 sm:space-y-3">
                          {industry.challenges.map((challenge, idx) => (
                            <li key={idx} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base text-red-700">
                              <i className="ri-close-circle-line text-red-500 mt-0.5 flex-shrink-0"></i>
                              {challenge}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-green-50 rounded-xl sm:rounded-2xl p-6 sm:p-8">
                        <h4 className="text-sm sm:text-base font-bold text-green-800 mb-4 sm:mb-4 flex items-center gap-2">
                          <i className="ri-shield-check-line text-lg sm:text-xl"></i>
                          Our Solutions
                        </h4>
                        <ul className="space-y-2 sm:space-y-3">
                          {industry.solutions.map((solution, idx) => (
                            <li key={idx} className="flex items-start gap-2 sm:gap-3 text-sm sm:text-base text-green-700">
                              <i className="ri-check-line text-green-500 mt-0.5 flex-shrink-0"></i>
                              {solution}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <button 
                      onClick={scrollToContact}
                      className="bg-[#0A1628] text-white px-8 sm:px-10 py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-[#D4A853] transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
                    >
                      Get {industry.name} Quote
                      <i className="ri-arrow-right-line"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-24 bg-gray-50" aria-labelledby="process-heading">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                HOW IT WORKS
              </span>
              <h2 id="process-heading" className="text-5xl font-bold text-[#0A1628] mb-4">
                Real-Time Protection Process
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                From detection to resolution in seconds—here's how our live monitoring keeps your property safe
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {[
                {
                  step: '01',
                  icon: 'ri-motion-line',
                  title: 'Motion Detected',
                  description: 'Advanced analytics detect movement and alert our monitoring center instantly.'
                },
                {
                  step: '02',
                  icon: 'ri-eye-line',
                  title: 'Live Verification',
                  description: 'Our trained operators immediately view live footage to assess the situation.'
                },
                {
                  step: '03',
                  icon: 'ri-volume-up-line',
                  title: 'Audio Warning',
                  description: 'If threat confirmed, operators issue verbal warnings through on-site speakers.'
                },
                {
                  step: '04',
                  icon: 'ri-police-car-line',
                  title: 'Authority Dispatch',
                  description: 'If intruder persists, we contact police with verified video evidence.'
                }
              ].map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-lg hover:shadow-xl transition-all h-full">
                    <span className="text-4xl sm:text-6xl font-extrabold text-[#D4A853]/20 absolute top-3 right-4 sm:top-4 sm:right-6">{item.step}</span>
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#D4A853] rounded-full flex items-center justify-center mb-6 sm:mb-6">
                      <i className={`${item.icon} text-xl sm:text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#0A1628] mb-3 sm:mb-3">{item.title}</h3>
                    <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{item.description}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-line text-2xl text-[#D4A853]"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 bg-[#0A1628]" aria-labelledby="features-heading">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="inline-block bg-[#D4A853] text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                MONITORING FEATURES
              </span>
              <h2 id="features-heading" className="text-5xl font-bold text-white mb-4">
                Complete Monitoring Capabilities
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Everything you need for comprehensive property protection
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
              {monitoringFeatures.map((feature, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-white/10 hover:bg-white/10 transition-all">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 bg-[#D4A853] rounded-lg sm:rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${feature.icon} text-xl sm:text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{feature.title}</h3>
                  <p className="text-white/70 text-xs sm:text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="py-24 bg-white" aria-labelledby="comparison-heading">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                WHY CHOOSE LIVE MONITORING
              </span>
              <h2 id="comparison-heading" className="text-5xl font-bold text-[#0A1628] mb-4">
                Live Monitoring vs. Traditional Security
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-gray-100 rounded-3xl p-10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-gray-400 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-600">Traditional Guards</h3>
                    <p className="text-gray-500 text-sm">On-site security personnel</p>
                  </div>
                </div>
                <ul className="space-y-4">
                  {[
                    'Limited to one location at a time',
                    'Human fatigue affects alertness',
                    'Higher ongoing labor costs',
                    'No permanent video record',
                    'Vulnerable to confrontation',
                    'Limited coverage area'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-gray-600">
                      <i className="ri-close-circle-line text-gray-400 text-xl mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#D4A853]/10 rounded-3xl p-10 border-2 border-[#D4A853]">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 bg-[#D4A853] rounded-full flex items-center justify-center">
                    <i className="ri-live-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#0A1628]">Live Video Monitoring</h3>
                    <p className="text-gray-600 text-sm">Professional remote surveillance</p>
                  </div>
                </div>
                <ul className="space-y-4">
                  {[
                    'Monitor entire property simultaneously',
                    'Consistent 24/7 alertness',
                    '50-70% cost savings',
                    'Complete video documentation',
                    'Safe remote intervention',
                    'Unlimited camera coverage'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-[#0A1628]">
                      <i className="ri-check-line text-[#D4A853] text-xl mt-0.5"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-gray-50" aria-labelledby="faq-heading">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-4 whitespace-nowrap">
                FAQ
              </span>
              <h2 id="faq-heading" className="text-4xl font-bold text-[#0A1628] mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">Common questions about live video monitoring</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
                >
                  <button 
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left cursor-pointer"
                  >
                    <span className="font-semibold text-[#0A1628] pr-4">{faq.question}</span>
                    <i className={`ri-arrow-down-s-line text-2xl text-[#D4A853] transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Free Consultation Form Section */}
        <section id="consultation-form" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Left - Info */}
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-6 py-2 rounded-full text-sm font-semibold mb-6 whitespace-nowrap">
                  FREE CONSULTATION
                </span>
                <h2 className="text-4xl sm:text-5xl font-bold text-[#0A1628] mb-6 leading-tight">
                  Request Your Free Site Assessment
                </h2>
                <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                  Tell us about your property and security needs. Our experts will design a custom live monitoring solution tailored to your industry — at no cost or obligation.
                </p>

                <div className="space-y-6 mb-10">
                  {[
                    { icon: 'ri-timer-line', title: 'Quick Response', desc: 'We\'ll get back to you within 2 business hours' },
                    { icon: 'ri-map-pin-line', title: 'On-Site Visit', desc: 'Free property walkthrough and risk assessment' },
                    { icon: 'ri-file-text-line', title: 'Custom Proposal', desc: 'Detailed monitoring plan with transparent pricing' },
                    { icon: 'ri-shield-check-line', title: 'No Obligation', desc: 'Zero pressure — the consultation is completely free' },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#D4A853]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <h4 className="text-base font-bold text-[#0A1628] mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-500">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-4 p-5 bg-[#0A1628] rounded-2xl">
                  <div className="w-12 h-12 bg-[#D4A853] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs mb-0.5">Prefer to talk now?</p>
                    <a href="tel:18884589181" className="text-white font-bold text-lg hover:text-[#D4A853] transition-colors cursor-pointer">
                      1-888-458-9181
                    </a>
                  </div>
                </div>
              </div>

              {/* Right - Form */}
              <div className="bg-gray-50 rounded-3xl p-8 sm:p-10 border border-gray-200">
                {formStatus === 'success' ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <i className="ri-check-line text-4xl text-green-600"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-3">Request Received!</h3>
                    <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                      Thank you for your interest. Our security team will contact you within 2 business hours to schedule your free site assessment.
                    </p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="text-[#D4A853] font-semibold hover:underline cursor-pointer whitespace-nowrap"
                    >
                      Submit another request
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-[#0A1628] mb-2">Get Your Free Quote</h3>
                    <p className="text-sm text-gray-500 mb-8">Fill out the form below and we'll reach out shortly.</p>

                    <form
                      id="live-monitoring-consultation"
                      data-readdy-form
                      onSubmit={handleFormSubmit}
                      className="space-y-5"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Full Name *</label>
                          <input
                            type="text"
                            name="fullName"
                            required
                            placeholder="John Smith"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-[#0A1628] mb-2">Phone Number *</label>
                          <input
                            type="tel"
                            name="phone"
                            required
                            placeholder="(416) 555-0123"
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="john@company.com"
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Company Name</label>
                        <input
                          type="text"
                          name="companyName"
                          placeholder="Your Company Inc."
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all"
                        />
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Industry Type *</label>
                        <input type="hidden" name="industry" value={selectedIndustry} />
                        <button
                          type="button"
                          onClick={() => setIndustryDropdownOpen(!industryDropdownOpen)}
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all cursor-pointer"
                        >
                          <span className={selectedIndustry ? 'text-[#0A1628]' : 'text-gray-400'}>
                            {selectedIndustry || 'Select your industry'}
                          </span>
                          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${industryDropdownOpen ? 'rotate-180' : ''}`}></i>
                        </button>
                        {industryDropdownOpen && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 max-h-56 overflow-y-auto">
                            {industryOptions.map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => {
                                  setSelectedIndustry(option);
                                  setIndustryDropdownOpen(false);
                                }}
                                className={`w-full text-left px-4 py-3 text-sm hover:bg-[#D4A853]/10 transition-colors cursor-pointer ${
                                  selectedIndustry === option ? 'text-[#D4A853] font-semibold bg-[#D4A853]/5' : 'text-gray-700'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#0A1628] mb-2">Property Details & Security Needs</label>
                        <textarea
                          name="propertyDetails"
                          rows={4}
                          maxLength={500}
                          placeholder="Describe your property, number of locations, current security setup, and any specific concerns..."
                          className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent transition-all resize-none"
                        ></textarea>
                        <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                      </div>

                      {formStatus === 'error' && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-3 rounded-xl text-sm">
                          <i className="ri-error-warning-line text-lg"></i>
                          Something went wrong. Please try again or call us directly.
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus === 'submitting' || !selectedIndustry}
                        className="w-full bg-[#D4A853] text-white py-4 rounded-full text-base font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {formStatus === 'submitting' ? (
                          <>
                            <i className="ri-loader-4-line animate-spin text-lg"></i>
                            Submitting...
                          </>
                        ) : (
                          <>
                            Request Free Consultation
                            <i className="ri-arrow-right-line text-lg"></i>
                          </>
                        )}
                      </button>

                      <p className="text-xs text-gray-400 text-center">
                        By submitting, you agree to our <Link to="/privacy-policy" className="underline hover:text-[#D4A853] cursor-pointer">Privacy Policy</Link>. We'll never share your information.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-[#D4A853] to-[#B8923D]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">Ready to Protect Your Property?</h2>
            <p className="text-xl text-white/90 mb-8">Get a free site assessment and custom monitoring proposal for your industry.</p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <button 
                onClick={scrollToContact}
                className="bg-white text-[#D4A853] px-10 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-xl inline-flex items-center gap-3"
              >
                <i className="ri-calendar-line text-xl"></i>
                Schedule Free Assessment
              </button>
              <a 
                href="tel:18884589181"
                className="border-2 border-white text-white px-10 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-3"
              >
                <i className="ri-phone-line text-xl"></i>
                1-888-458-9181
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex flex-col leading-none">
                <span className="text-xl sm:text-2xl font-extrabold tracking-wider text-[#D4A853]" style={{ fontFamily: "'Inter', sans-serif" }}>ALLIANCE</span>
                <span className="text-[8px] sm:text-[10px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
              </div>
            </Link>
            <p className="text-white/60 text-sm">© 2024 Security Solutions. All rights reserved.</p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
